import { Percent, Tags, Timer, XCircle } from 'lucide-react';
import React from 'react';

import { KpiGrid } from '../KpiCard';
import { ReportKpiCard, ReportKpiPairCard } from '../report-cards';
import { LegacyTable, ReportSection, type LegacyCell } from '../ReportSection';

import { lostAt, num, ratio, topPivotColumn, withPct } from './band-helpers';

import type { BandProps } from './band-props';
import type { AgentTrackerRow, LeadListQuery } from '@utazzo/shared';

export const LostLeadsBand = ({
  report,
  totals,
  scopedRow,
  scopedLost,
  scopedLostCurrent,
  loading,
  dash,
  agentEntries,
  agentCell,
  expandAllAction,
  drillCell,
  from,
  to,
  lostReasonPivotData,
  lostReasonPivotLoading,
  lostFilter,
  setLostFilter,
}: BandProps): React.JSX.Element => {
  // ----- Lost tab drills. Cohort = leads LOST this period (closed_at window),
  // any creation date, matching the tab's combined "Total Lost". -----
  const lostBase: Partial<LeadListQuery> = {
    currentBucket: 'LOST',
    closedAtFrom: from,
    closedAtTo: to,
  };
  // "Lost at X Stage" cell -> leads lost this period out of that sub-status.
  const lostStageCell = (
    count: number,
    agentId: string | null,
    sub: { code: string; name: string },
    denom: number,
  ): LegacyCell =>
    drillCell(withPct(count, denom), count, count, `Lost at ${sub.name} stage`, {
      ...lostBase,
      ...(agentId ? { assignedAgentId: agentId } : {}),
      lostFromSubStatusCode: sub.code as 'proposal' | 'warm' | 'hot' | 'before_active',
    });
  // One agent's Lost Lead Stage cells: each active sub-status (count + avg
  // attempts), then the complementary "before Active (NQ)" bucket, so the row
  // reconciles to that agent's Total Lost.
  const NQ_STAGE = { code: 'before_active', name: 'before Active (NQ)' };
  const lostStageRow = (r: AgentTrackerRow): LegacyCell[] => {
    const id = (r.agentId as string | null) ?? null;
    const attempts = (e: { count: number; followUps: number }): number =>
      e.count > 0 ? Math.round(e.followUps / e.count) : 0;
    // Each "lost at X stage" cell's % is of this agent's total lost (sum of all
    // stage buckets incl. the before-Active/NQ complement), so the row shows
    // how that agent's losses divide across stages.
    const totalLost =
      (report?.subStatuses.reduce((sum, sub) => sum + lostAt(r, sub.code).count, 0) ?? 0) +
      lostAt(r, 'before_active').count;
    return [
      ...(report?.subStatuses.flatMap((sub): LegacyCell[] => {
        const le = lostAt(r, sub.code);
        return [lostStageCell(le.count, id, sub, totalLost), attempts(le)];
      }) ?? []),
      lostStageCell(lostAt(r, 'before_active').count, id, NQ_STAGE, totalLost),
      attempts(lostAt(r, 'before_active')),
    ];
  };
  // Lost Reasons pivot cell. `reasonId`: a uuid -> that reason; 'ALL' -> the
  // Total column (no reason filter); null -> the "Others" bucket (not
  // drillable). `agentId` null -> the totals row (all agents). `denom` is the
  // row's own total lost (that agent's total, or the grand total on the Total
  // row), so each reason cell also shows its share - how that row's losses
  // divide across reasons (events). The Total column ('ALL') is the 100%
  // denominator, so no % is shown on it.
  const lostCell = (
    count: number,
    agentId: string | null,
    reasonId: string | null | 'ALL',
    title: string,
    denom: number,
  ): LegacyCell => {
    const display = withPct(count, reasonId === 'ALL' ? null : denom);
    if (reasonId === null) return { node: display, sort: count };
    return drillCell(display, count, count, title, {
      ...lostBase,
      ...(agentId ? { assignedAgentId: agentId } : {}),
      ...(reasonId !== 'ALL' ? { lostReasonId: reasonId } : {}),
    });
  };

  return (
    <div className="space-y-4 pt-2">
      <KpiGrid>
        <ReportKpiCard
          icon={XCircle}
          tone="danger"
          value={scopedLost ?? dash}
          label="Total Lost Leads"
          hint={
            scopedRow
              ? `${num(scopedLostCurrent ?? 0)} current + ${num(scopedRow.carriedIn.currentLost)} carry fwd`
              : undefined
          }
        />
        <ReportKpiCard
          icon={Tags}
          tone="warning"
          value={topPivotColumn(lostReasonPivotData)}
          label="Top Lost Reason"
        />
        <ReportKpiCard
          icon={Percent}
          value={
            scopedRow && scopedLost !== undefined
              ? ratio(scopedLost, scopedRow.totalLeads + scopedRow.carriedInLeads)
              : dash
          }
          label="Lost % of Total Leads"
        />
        <ReportKpiPairCard
          icon={Timer}
          tone="danger"
          label="Avg. Period of Loss"
          className="col-span-2"
          parts={[
            {
              label: 'Total',
              value: scopedRow
                ? scopedRow.avgLossDays !== null
                  ? `${scopedRow.avgLossDays}d`
                  : '-'
                : dash,
            },
            {
              label: 'From NQ',
              value: scopedRow
                ? scopedRow.avgLossDaysNq !== null
                  ? `${scopedRow.avgLossDaysNq}d`
                  : '-'
                : dash,
            },
            {
              label: 'From Active',
              value: scopedRow
                ? scopedRow.avgLossDaysActive !== null
                  ? `${scopedRow.avgLossDaysActive}d`
                  : '-'
                : dash,
            },
            {
              label: 'NCL',
              value: scopedRow
                ? scopedRow.avgLossDaysNonContact !== null
                  ? `${scopedRow.avgLossDaysNonContact}d`
                  : '-'
                : dash,
            },
          ]}
        />
      </KpiGrid>

      <ReportSection title="Lost Lead Stage" actions={expandAllAction}>
        <LegacyTable
          loading={loading || !report}
          headers={[
            'Agent',
            ...(report?.subStatuses.flatMap((sub) => [
              `Lost at ${sub.name} Stage`,
              `${sub.name} Attempts`,
            ]) ?? []),
            'Lost before Active (NQ)',
            'NQ Attempts',
          ]}
          rows={agentEntries.map(({ row, ...entry }) => [
            { node: agentCell({ row, ...entry }), sort: row.agentName },
            ...lostStageRow(row),
          ])}
          totals={totals ? ['Total', ...lostStageRow(totals)] : undefined}
        />
      </ReportSection>

      <ReportSection
        title="Lost Reasons"
        toolbar={
          <div className="text-foreground flex flex-wrap items-center gap-4 text-sm">
            <label className="flex items-center gap-1.5">
              <input
                type="radio"
                name="lostSubStatus"
                checked={lostFilter === 'all'}
                onChange={() => setLostFilter('all')}
              />
              All
            </label>
            {(report?.subStatuses ?? []).map((sub) => (
              <label key={sub.code} className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="lostSubStatus"
                  checked={lostFilter === sub.code}
                  onChange={() => setLostFilter(sub.code)}
                />
                {sub.name}
              </label>
            ))}
          </div>
        }
      >
        <LegacyTable
          loading={lostReasonPivotLoading || !lostReasonPivotData}
          emptyText="No leads in this range."
          headers={['Agent', ...(lostReasonPivotData?.columns ?? []), 'Total']}
          rows={(lostReasonPivotData?.rows ?? []).map((agentName, rowIndex) => {
            const pivot = lostReasonPivotData;
            const agentId = pivot?.rowIds[rowIndex] ?? null;
            // Each reason cell's % is of THIS agent's total lost, so an agent
            // row shows how their own losses divide across reasons.
            const rowTotal = pivot?.rowTotals[rowIndex] ?? 0;
            return [
              agentName,
              ...(pivot?.columns ?? []).map((col, colIndex) =>
                lostCell(
                  pivot?.cells[rowIndex]?.[colIndex] ?? 0,
                  agentId,
                  pivot?.columnIds[colIndex] ?? null,
                  `${agentName} - ${col}`,
                  rowTotal,
                ),
              ),
              lostCell(rowTotal, agentId, 'ALL', `${agentName} - all lost`, rowTotal),
            ];
          })}
          totals={
            lostReasonPivotData && [
              'Total',
              ...lostReasonPivotData.columns.map((col, colIndex) =>
                lostCell(
                  lostReasonPivotData.columnTotals[colIndex] ?? 0,
                  null,
                  lostReasonPivotData.columnIds[colIndex] ?? null,
                  `All agents - ${col}`,
                  lostReasonPivotData.grandTotal,
                ),
              ),
              lostCell(
                lostReasonPivotData.grandTotal,
                null,
                'ALL',
                'All lost this period',
                lostReasonPivotData.grandTotal,
              ),
            ]
          }
        />
      </ReportSection>
    </div>
  );
};
